import axios from 'axios'
import { getTokenFromURL } from '../utils/tokenHandler.js'

/**
 * Optix API Service
 * Handles all communication with the Optix API
 */

// Base URL for Optix API - update this with your actual Optix API endpoint
const OPTIX_API_BASE_URL = import.meta.env.VITE_OPTIX_API_URL || 'https://api.catalufa.net'

/**
 * Creates an axios instance configured for Optix API calls
 */
function createOptixClient() {
  const token = getTokenFromURL()
  
  if (!token) {
    throw new Error('Optix API token not found in URL parameters')
  }
  
  return axios.create({
    baseURL: OPTIX_API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
}

/**
 * Fetches data from Optix API
 * @param {string} endpoint - API endpoint (e.g., '/data', '/organizations')
 * @param {Object} params - Query parameters
 * @returns {Promise} Object with data and headers
 */
export async function fetchOptixData(endpoint, params = {}) {
  try {
    const client = createOptixClient()
    const response = await client.get(endpoint, { params })
    return {
      data: response.data,
      headers: response.headers
    }
  } catch (error) {
    console.error('Optix API Error:', error)
    throw error
  }
}

/**
 * Posts data to Optix API
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Data to post
 * @returns {Promise} Object with data and headers
 */
export async function postOptixData(endpoint, data) {
  try {
    const client = createOptixClient()
    const response = await client.post(endpoint, data)
    return {
      data: response.data,
      headers: response.headers
    }
  } catch (error) {
    console.error('Optix API Error:', error)
    throw error
  }
}

/**
 * Extracts operation name from GraphQL query string for Network tab identification
 */
function getOperationLabel(query, operationName) {
  if (operationName) return operationName
  const match = query.match(/(?:query|mutation)\s+(\w+)/)
  return match ? match[1] : 'query'
}

/**
 * Executes a GraphQL query against the Optix API
 * @param {string} query - GraphQL query string
 * @param {Object} variables - GraphQL variables (optional)
 * @param {string} operationName - GraphQL operation name (optional)
 * @returns {Promise} Object with data and headers
 */
export async function executeGraphQLQuery(query, variables = {}, operationName = null) {
  try {
    const client = createOptixClient()
    const graphqlEndpoint = '/graphql'
    const params = { adminQuery: getOperationLabel(query, operationName) }
    
    const payload = {
      query,
      ...(variables && Object.keys(variables).length > 0 && { variables }),
      ...(operationName && { operationName })
    }
    
    const response = await client.post(graphqlEndpoint, payload, { params })
    return {
      data: response.data,
      headers: response.headers
    }
  } catch (error) {
    console.error('Optix GraphQL Error:', error)
    if (error.response) {
      console.error('GraphQL Response:', error.response.data)
      console.error('Status:', error.response.status)
    }
    throw error
  }
}

/**
 * Executes multiple GraphQL queries in a single batch request
 * @param {Array<Object>} queries - Array of query objects with {query, variables, operationName}
 * @returns {Promise} Object with data array and headers
 */
export async function executeBatchGraphQLQueries(queries) {
  try {
    const client = createOptixClient()
    const graphqlEndpoint = '/graphql'
    
    // Format queries for batch request (no adminQuery - can cause server issues)
    const batchPayload = queries.map(({ query, variables = {}, operationName = null }) => ({
      query,
      ...(variables && Object.keys(variables).length > 0 && { variables }),
      ...(operationName && { operationName })
    }))
    
    const response = await client.post(graphqlEndpoint, batchPayload)
    return {
      data: response.data,
      headers: response.headers
    }
  } catch (error) {
    console.error('Optix Batch GraphQL Error:', error)
    if (error.response) {
      console.error('Batch GraphQL Response:', error.response.data)
      console.error('Status:', error.response.status)
    }
    throw error
  }
}

/**
 * Fetches organization details (ID, name, logo) from Optix API.
 * Routes through dataFetcher for caching, dedup, and batching with other init queries.
 * @returns {Promise} Organization data
 */
const _orgDetailsQuery = `
  query GetOrgIDandName {
    organization {
      organization_id
      subdomain
      name
      optix_billing {
        signed_up_timestamp
        has_signed_up
      }
      square_logo {
        url
      }
    }
  }
`
export async function getOrganizationDetails() {
  const { dataFetcher } = await import('./dataFetcher.js')
  const result = await dataFetcher.fetchGraphQL(_orgDetailsQuery, {}, 'GetOrgIDandName', 10, true)
  return { data: { organization: result.organization } }
}
